"use client";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Block } from "@/lib/api/blocks";
import { formatRelativeTime } from "@/lib/utils";
import { IconBlocks, IconEyeOpen, IconGlobe } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconLockOutline18, IconSquareInfoOutline18 } from "nucleo-ui-outline-18";
import { useMemo } from "react";
interface BlocksUsageTableProps {
  blocks: Block[];
  isLoading: boolean;
}
export function BlocksUsageTable({
  blocks,
  isLoading
}: BlocksUsageTableProps) {
  const sorted = useMemo(() => [...blocks].sort((a, b) => b.usageCount - a.usageCount), [blocks]);
  if (isLoading) {
    return <div className="space-y-3">
        {Array.from({
        length: 5
      }).map((_, i) => <div key={i} className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-16 animate-pulse rounded-lg" />)}
      </div>;
  }
  if (blocks.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <IconBlocks className="dark:text-ds-text-subtle h-10 w-10 text-zinc-300" />
        <p className="body-sm-medium mt-3">No blocks yet</p>
        <p className="label-xs mt-1">Create reusable blocks to see usage data here</p>
      </div>;
  }
  return <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-ds-border-default border-b">
            <th className="micro-label pb-3 pr-4 text-left">Block</th>
            <th className="micro-label pb-3 pr-4 text-left">Version</th>
            <th className="micro-label pb-3 pr-4 text-left">Status</th>
            <th className="micro-label pb-3 pr-4 text-right">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center gap-1">
                    Usage
                    <IconSquareInfoOutline18 className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-center">
                  <p>Includes active workflow references and workflow executions</p>
                </TooltipContent>
              </Tooltip>
            </th>
            <th className="micro-label pb-3 pr-4 text-right">Forks</th>
            <th className="micro-label pb-3 pr-4 text-left">Scope</th>
            <th className="micro-label pb-3 text-left">Updated</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(block => <tr key={block.id} className="border-ds-neutral-100 dark:border-ds-neutral-800/50 dark:hover:bg-ds-neutral-800/30 hover:bg-ds-surface-hover border-b transition-colors">
              {/* Block name */}
              <td className="py-3 pr-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400">
                    <IconBlocks className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="body-sm-medium truncate">{block.name}</p>
                    {block.description && <p className="label-xs truncate">{block.description}</p>}
                  </div>
                </div>
              </td>

              {/* Version */}
              <td className="py-3 pr-4">
                <Badge variant="orange">v{block.version}</Badge>
              </td>

              {/* Status */}
              <td className="py-3 pr-4">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${block.status === "published" ? "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400" : block.status === "draft" ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400" : "bg-ds-neutral-100 text-ds-text-muted dark:bg-ds-neutral-800"}`}>
                  {block.status}
                </span>
              </td>

              {/* Usage count */}
              <td className="py-3 pr-4 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <IconEyeOpen className="text-ds-text-disabled h-3 w-3" />
                  <span className="body-sm-semibold">{block.usageCount}</span>
                </div>
              </td>

              {/* Fork count */}
              <td className="py-3 pr-4 text-right">
                <span className="body-sm">{block.forkCount}</span>
              </td>

              {/* Scope */}
              <td className="py-3 pr-4">
                <div className="flex items-center gap-1.5">
                  {block.visibility === "public" ? <>
                      <IconGlobe className="h-3 w-3 text-blue-500" />
                      <span className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Public</span>
                    </> : <>
                      <IconLockOutline18 className="text-ds-text-disabled h-3 w-3" />
                      <span className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Org</span>
                    </>}
                </div>
              </td>

              {/* Updated */}
              <td className="py-3">
                <span className="label-xs">{formatRelativeTime(new Date(block.updatedAt))}</span>
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}